// Paie du mois : préparation des bulletins, saisie des éléments variables, validation, paiement.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem,
  Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight, EditOutlined, CheckOutlined, PrintOutlined, LockOpenOutlined, PaymentsOutlined, DeleteOutline, FileDownloadOutlined } from '@mui/icons-material';
import Layout from '../../components/Layout';
import { comptaAPI } from '../../services/api';
import { apiError, dateFr, fcfa, today, PAYMENT_MODES } from '../../utils/format';
import { downloadCsv, duree } from '../../utils/rh';
import { tokens } from '../../theme';
import { Kpi } from '../../components/ui';

const STATUT = { BROUILLON: { label: 'Brouillon', color: 'default' }, VALIDE: { label: 'Validé', color: 'info' }, PAYE: { label: 'Payé', color: 'success' } };
const GAINS = [['salaire_base', 'Salaire de base'], ['indemnites', 'Indemnités'], ['primes', 'Primes'], ['heures_sup', 'Heures supplémentaires']];
const RETENUES = [['retenue_absences', 'Retenue absences'], ['ipres', 'IPRES (part salariale)'], ['ipm', 'IPM'], ['ir', 'Impôt sur le revenu (IR)'], ['trimf', 'TRIMF'], ['avances', 'Avances / acomptes'], ['autres_retenues', 'Autres retenues']];
const num = (v) => Number(String(v ?? '').replace(/[\s  ]/g, '').replace(',', '.')) || 0;
const shift = (p, d) => { const [y, m] = p.split('-').map(Number); const dt = new Date(Date.UTC(y, m - 1 + d, 1)); return dt.toISOString().slice(0, 7); };
const label = (p) => new Date(`${p}-01T00:00:00Z`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });

const Paie = () => {
  const [periode, setPeriode] = useState(today().slice(0, 7));
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const [pay, setPay] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(() => { comptaAPI.paie(periode).then((r) => setD(r.data)).catch((e) => setError(apiError(e))); }, [periode]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setError('');
    try { const r = await fn(); setToast(r.data.message); load(); } catch (e) { setError(apiError(e)); }
  };
  const saveEdit = async () => {
    try {
      const body = Object.fromEntries([...GAINS, ...RETENUES].map(([k]) => [k, num(edit[k])]));
      await comptaAPI.saveBulletin(edit.id, { ...body, commentaire: edit.commentaire });
      setEdit(null); setToast('Bulletin enregistré'); load();
    } catch (e) { setEdit({ ...edit, error: apiError(e) }); }
  };
  const paySingleOrAll = async () => {
    try {
      const r = pay.id
        ? await comptaAPI.actionBulletin(pay.id, 'payer', pay)
        : await comptaAPI.payerPaie(periode, { ...pay, mode: pay.mode || undefined });
      setPay(null); setToast(r.data.message); load();
    } catch (e) { setPay({ ...pay, error: apiError(e) }); }
  };
  const exportCsv = () => downloadCsv(`paie_${periode}.csv`,
    ['Matricule', 'Nom', 'Prénom', ...GAINS.map(([, l]) => l), ...RETENUES.map(([, l]) => l), 'Brut', 'Net à payer', 'Mode', 'Banque', 'Compte', 'Statut', 'Payé le'],
    d.bulletins.map((b) => [b.matricule, b.nom, b.prenom, ...GAINS.map(([k]) => b[k]), ...RETENUES.map(([k]) => b[k]), b.brut, b.net,
      PAYMENT_MODES[b.mode_paiement] || '', b.banque || '', b.compte || '', STATUT[b.statut].label, b.date_paiement ? dateFr(b.date_paiement) : '']));

  const brutEdit = edit ? GAINS.reduce((s, [k]) => s + num(edit[k]), 0) - num(edit.retenue_absences) : 0;
  const netEdit = edit ? brutEdit - RETENUES.slice(1).reduce((s, [k]) => s + num(edit[k]), 0) : 0;
  const nbValides = d?.bulletins.filter((b) => b.statut === 'VALIDE').length || 0;
  const sansSalaire = d?.sans_bulletin.filter((e) => !e.salaire_base) || [];

  return (
    <Layout title="Paie du mois" plain
      actions={d && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<FileDownloadOutlined />} disabled={!d.bulletins.length} onClick={exportCsv}>Exporter (Excel)</Button>
          <Button variant="outlined" startIcon={<PrintOutlined />} disabled={!d.bulletins.length} onClick={() => window.open(`/compta/paie/mois/${periode}/imprimer`, '_blank')}>Imprimer les bulletins</Button>
          <Button variant="outlined" disabled={!d.sans_bulletin.some((e) => e.salaire_base > 0)} onClick={() => act(() => comptaAPI.preparerPaie(periode))}>Préparer les bulletins</Button>
          <Button variant="contained" startIcon={<PaymentsOutlined />} disabled={!nbValides} onClick={() => setPay({ date_paiement: today(), mode: '', reference: '', error: '' })}>
            Payer les bulletins validés ({nbValides})
          </Button>
        </Box>
      )}>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton aria-label="Mois précédent" onClick={() => setPeriode(shift(periode, -1))}><ChevronLeft /></IconButton>
        <Typography sx={{ fontWeight: 600, minWidth: 150, textAlign: 'center', textTransform: 'capitalize' }}>{label(periode)}</Typography>
        <IconButton aria-label="Mois suivant" onClick={() => setPeriode(shift(periode, 1))}><ChevronRight /></IconButton>
      </Box>

      {d && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[['Masse salariale brute', d.totaux.brut, 'navy'], ['Retenues', d.totaux.retenues, 'red'], ['Net à verser', d.totaux.net, 'navy'], ['Déjà payé', d.totaux.net_paye, 'green'], ['Reste à payer', d.totaux.net_a_payer, 'amber']].map(([l, v, t]) => (
              <Grid item xs={6} md key={l}><Kpi compact tint={t} label={l} value={fcfa(v)} sub="FCFA" valueSize={{ xs: 18, sm: 21 }} /></Grid>
            ))}
          </Grid>
          {d.bulletins.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Aucun bulletin pour ce mois. « Préparer les bulletins » crée un brouillon par employé ayant un salaire, avec une retenue proposée pour les absences injustifiées relevées par les RH.
            </Alert>
          )}
          {sansSalaire.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>Sans salaire renseigné (menu Salaires) : {sansSalaire.map((e) => `${e.prenom} ${e.nom}`).join(', ')}</Alert>
          )}
          <Card variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employé</TableCell><TableCell>Présence (RH)</TableCell><TableCell align="right">Brut</TableCell>
                    <TableCell align="right">Retenues</TableCell><TableCell align="right">Net à payer</TableCell><TableCell>Statut</TableCell><TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {d.bulletins.map((b) => {
                    const ret = b.cotisations + b.impots + b.avances + b.autres_retenues;
                    const p = b.presence;
                    return (
                      <TableRow key={b.id} hover>
                        <TableCell><b>{b.nom} {b.prenom}</b><br /><span style={{ fontSize: 12, color: '#5B6675' }}>{b.matricule}{b.poste ? ` · ${b.poste}` : ''}</span></TableCell>
                        <TableCell sx={{ fontSize: 12.5 }}>
                          {p ? (() => {
                            const items = [
                              p.absences_injustifiees ? <Box key="a" component="span" sx={{ color: 'error.main' }}>{p.absences_injustifiees} abs. injustifiée(s)</Box> : null,
                              p.non_pointes ? <Box key="n" component="span" sx={{ color: 'warning.main' }}>{p.non_pointes} non pointé(s)</Box> : null,
                              p.retards ? <span key="r">{p.retards} retard(s) ({duree(p.minutes_retard)})</span> : null,
                              p.conges ? <span key="c">{p.conges} j congé</span> : null,
                            ].filter(Boolean);
                            return items.length ? items.reduce((acc, x, i) => (i ? [...acc, ' · ', x] : [x]), []) : <span style={{ color: '#9AA3AF' }}>RAS</span>;
                          })() : '—'}
                          {b.retenue_absences > 0 && <Box sx={{ color: 'text.secondary' }}>Retenue : {fcfa(b.retenue_absences)} F ({b.jours_absence} j)</Box>}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{fcfa(b.brut)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{fcfa(ret)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap', fontWeight: 700 }}>{fcfa(b.net)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={STATUT[b.statut].label} color={STATUT[b.statut].color} />
                          {b.statut === 'PAYE' && <Box sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>{dateFr(b.date_paiement)} · {PAYMENT_MODES[b.mode_paiement]}</Box>}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Bulletin de paie"><IconButton size="small" aria-label={`Bulletin de paie ${b.nom}`} onClick={() => window.open(`/compta/paie/bulletins/${b.id}/imprimer`, '_blank')}><PrintOutlined fontSize="small" /></IconButton></Tooltip>
                          {b.statut === 'BROUILLON' && (
                            <>
                              <Tooltip title="Modifier"><IconButton size="small" aria-label={`Modifier le bulletin ${b.nom}`} onClick={() => setEdit({ ...b, error: '' })}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Valider"><IconButton size="small" color="primary" aria-label={`Valider ${b.nom}`} onClick={() => act(() => comptaAPI.actionBulletin(b.id, 'valider'))}><CheckOutlined fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Supprimer"><IconButton size="small" aria-label={`Supprimer le bulletin ${b.nom}`} onClick={() => window.confirm('Supprimer ce bulletin ?') && act(() => comptaAPI.deleteBulletin(b.id))}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                            </>
                          )}
                          {b.statut === 'VALIDE' && (
                            <>
                              <Tooltip title="Rouvrir pour modifier"><IconButton size="small" aria-label={`Rouvrir ${b.nom}`} onClick={() => act(() => comptaAPI.actionBulletin(b.id, 'rouvrir'))}><LockOpenOutlined fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Payer"><IconButton size="small" color="success" aria-label={`Payer ${b.nom}`} onClick={() => setPay({ id: b.id, nom: `${b.prenom} ${b.nom}`, net: b.net, date_paiement: today(), mode: b.mode_paiement || 'VIREMENT', reference: '', error: '' })}><PaymentsOutlined fontSize="small" /></IconButton></Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Les cotisations et impôts sont saisis par le comptable : aucun taux légal n'est appliqué automatiquement.
          </Typography>
        </>
      )}

      <Dialog open={!!edit} onClose={() => setEdit(null)} fullWidth maxWidth="sm">
        {edit && (
          <>
            <DialogTitle>Bulletin de {edit.prenom} {edit.nom} — {label(periode)}</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              {edit.error && <Alert severity="error" sx={{ mb: 2 }}>{edit.error}</Alert>}
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Gains</Typography>
                  {GAINS.map(([k, l]) => <TextField key={k} fullWidth size="small" label={l} value={edit[k]} sx={{ mb: 1.5 }} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} />)}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Retenues <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>(détaillées sur le bulletin)</Typography></Typography>
                  {RETENUES.map(([k, l]) => <TextField key={k} fullWidth size="small" label={l} value={edit[k]} sx={{ mb: 1.5 }} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} />)}
                </Grid>
              </Grid>
              <TextField fullWidth size="small" label="Commentaire" value={edit.commentaire || ''} onChange={(e) => setEdit({ ...edit, commentaire: e.target.value })} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, fontFamily: tokens.mono }}>
                <span>Brut : <b>{fcfa(brutEdit)} F</b></span><span>Net à payer : <b style={{ color: netEdit < 0 ? '#B4232A' : undefined }}>{fcfa(netEdit)} F</b></span>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEdit(null)}>Annuler</Button>
              <Button variant="contained" onClick={saveEdit}>Enregistrer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={!!pay} onClose={() => setPay(null)} fullWidth maxWidth="xs">
        {pay && (
          <>
            <DialogTitle>{pay.id ? `Payer ${pay.nom} (${fcfa(pay.net)} F)` : `Payer ${nbValides} bulletin(s) validé(s)`}</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
              {pay.error && <Alert severity="error">{pay.error}</Alert>}
              <TextField type="date" label="Date de paiement" InputLabelProps={{ shrink: true }} value={pay.date_paiement} onChange={(e) => setPay({ ...pay, date_paiement: e.target.value })} />
              <TextField select label="Mode" value={pay.mode} onChange={(e) => setPay({ ...pay, mode: e.target.value })}>
                {!pay.id && <MenuItem value="">Celui de chaque fiche salarié</MenuItem>}
                {Object.entries(PAYMENT_MODES).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
              </TextField>
              <TextField label="Référence (n° de virement, chèque…)" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPay(null)}>Annuler</Button>
              <Button variant="contained" color="success" onClick={paySingleOrAll}>Confirmer le paiement</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
};

export default Paie;
